import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation
} from 'react-router-dom';

// Import screens
import HomeScreen from '../components/screens/HomeScreen';
import AllSportsScreen from '../components/screens/AllSportsScreen';
import LiveGamesScreen from '../components/screens/LiveGamesScreen';
import HandicappersScreen from '../components/screens/HandicappersScreen';
import PromosScreen from '../components/screens/PromosScreen';
import PodcastsScreen from '../components/screens/PodcastsScreen';
import ChatScreen from '../components/screens/ChatScreen';
import AccountScreen from '../components/screens/AccountScreen';
import AffiliateDashboardScreen from '../components/screens/AffiliateDashboardScreen';
import ReferralRedirectScreen from '../components/screens/ReferralRedirectScreen';
import SettingsScreen from '../components/screens/SettingsScreen';
import AboutUsScreen from '../components/screens/AboutUsScreen';
import PrivacyPolicyScreen from '../components/screens/PrivacyPolicyScreen';
import TermsConditionsScreen from '../components/screens/TermsConditionsScreen';
import ContactUsScreen from '../components/screens/ContactUsScreen';
import HelpScreen from '../components/screens/HelpScreen';
import ResourcesScreen from '../components/screens/ResourcesScreen';
import LoginScreen from '../components/screens/LoginScreen';
import ProfileScreen from '../components/screens/ProfileScreen';
import AccountLinkScreen from '../components/screens/AccountLinkScreen';
import AdminDashboardScreen from '../components/screens/AdminDashboardScreen';
import HandicapperDashboardScreen from '../components/screens/HandicapperDashboardScreen';
import HandicapperPicksScreen from '../components/screens/HandicapperPicksScreen';
import UnauthorizedScreen from '../components/screens/UnauthorizedScreen';
import NotFoundScreen from '../components/screens/NotFoundScreen';
import SportHubScreen from '../components/screens/SportHubScreen';
import MainOddsScreen from '../components/screens/MainOddsScreen';
import TokenDebug from '../components/TokenDebug';
import EmailActionHandler from '../components/screens/EmailActionHandler';
import BettorAIScreen from '../components/screens/BettorAIScreen';
import SubscriptionScreen from '../components/Subscription/SubscriptionScreen';
import PropsGameListScreen from '../components/screens/PropsGameListScreen';
import PlayerPropsScreen from '../components/screens/PlayerPropsScreen';
import ParlayBuilderScreen from '../components/screens/ParlayBuilderScreen';
import ParlayRecordScreen from '../components/screens/ParlayRecordScreen';
import BetTrackerScreen from '../components/screens/BetTrackerScreen';
import DeleteAccountScreen from '../components/screens/DeleteAccountScreen';

// Layout component for authenticated routes
import AppLayout from '../components/layouts/AppLayout';

// Protected route component
import { ProtectedRoute } from '../components/ProtectedRoute';
import { ROLES } from '../services/roleService';

// Login route: a signed-in user who lands here is sent on to where they
// were headed (e.g. back to /subscription after signing in from its guest
// view, which passes state: { from: '/subscription' }). The destination is
// also kept in sessionStorage because the router is rebuilt when auth
// state changes, which can drop the navigation state.
const LoginRoute = ({ user }) => {
  const location = useLocation();

  useEffect(() => {
    if (!user && location.state?.from) {
      try {
        sessionStorage.setItem('postLoginFrom', location.state.from);
      } catch {
        // sessionStorage unavailable; the navigation-state path may still work.
      }
    }
  }, [user, location.state]);

  if (user) {
    let from = location.state?.from;
    try {
      if (!from) {
        from = sessionStorage.getItem('postLoginFrom');
      }
      sessionStorage.removeItem('postLoginFrom');
    } catch {
      // Ignore storage errors; fall through to the default destination.
    }
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }
  return <LoginScreen />;
};

// Route definitions
export const createAppRouter = (user, odds, loading, error, onLoadOdds) => {
  return createBrowserRouter([
    {
      path: "/login",
      element: <LoginRoute user={user} />
    },
    {
      // Referral links (bettoradvantage.com/r/CODE): validated, stored
      // for the signup form, click-logged, then on to signup. Standalone
      // (no AppLayout) so nothing slows the redirect down.
      path: "/r/:code",
      element: <ReferralRedirectScreen />
    },
    {
      path: "/auth/action",
      element: <EmailActionHandler />
    },
    {
      path: "/",
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: (
            <HomeScreen
              odds={odds}
              loading={loading}
              error={error}
              onLoadOdds={onLoadOdds}
            />
          )
        },
        {
          path: "allsports",
          element: <AllSportsScreen />
        },
        {
          path: "odds",
          element: (
            <MainOddsScreen
              odds={odds}
              loading={loading}
              error={error}
              onLoadOdds={onLoadOdds}
            />
          )
        },
        {
          path: "sport/:sportKey",
          element: (
            <SportHubScreen
              odds={odds}
              loading={loading}
              error={error}
              onLoadOdds={onLoadOdds}
            />
          )
        },
        {
          path: "sport/:sportKey/:section",
          element: (
            <SportHubScreen
              odds={odds}
              loading={loading}
              error={error}
              onLoadOdds={onLoadOdds}
            />
          )
        },
        {
          path: "404",
          element: <NotFoundScreen />
        },
        {
          path: "live-games",
          element: <LiveGamesScreen />
        },
        {
          path: "handicappers",
          element: <HandicappersScreen />
        },
        {
          path: "promos",
          element: <PromosScreen />
        },
        {
          path: "podcasts",
          element: <PodcastsScreen />
        },
        {
          path: "chat",
          element: user ? <ChatScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "account",
          element: user ? <AccountScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "profile",
          element: user ? <ProfileScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "affiliate",
          element: user ? <AffiliateDashboardScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "settings",
          element: user ? <SettingsScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "subscription",
          element: <SubscriptionScreen />
        },
        {
          path: "delete-account",
          element: user ? <DeleteAccountScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "help",
          element: user ? <HelpScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "resources",
          element: <ResourcesScreen />
        },
        {
          path: "about",
          element: <AboutUsScreen />
        },
        {
          path: "privacy",
          element: <PrivacyPolicyScreen />
        },
        {
          path: "terms",
          element: <TermsConditionsScreen />
        },
        {
          path: "contact",
          element: <ContactUsScreen />
        },
        {
          path: "token-debug",
          element: <TokenDebug />
        },
        {
          path: "admin",
          element: (
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <AdminDashboardScreen />
            </ProtectedRoute>
          )
        },
        {
          path: "bettor-ai",
          element: (
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <BettorAIScreen />
            </ProtectedRoute>
          )
        },
        {
          path: "handicapper-dashboard",
          element: (
            <ProtectedRoute requiredRole={ROLES.HANDICAPPER}>
              <HandicapperDashboardScreen
                odds={odds}
                loading={loading}
                error={error}
                onLoadOdds={onLoadOdds}
              />
            </ProtectedRoute>
          )
        },
        {
          path: "player-props",
          element: <PropsGameListScreen />
        },
        {
          path: "player-props/:sportKey/:eventId",
          element: <PlayerPropsScreen />
        },
        {
          path: "parlay-builder",
          element: <ParlayBuilderScreen />
        },
        {
          // Public on purpose: the parlay record is the marketing —
          // no login or Premium guard (data comes from the
          // parlay-record function, not client Firestore reads).
          path: "parlay-record",
          element: <ParlayRecordScreen />
        },
        {
          path: "bet-tracker",
          element: user ? <BetTrackerScreen /> : <Navigate to="/login" replace />
        },
        {
          path: "handicappers/:handicapperName/picks",
          element: <HandicapperPicksScreen />
        },
        {
          path: "unauthorized",
          element: <UnauthorizedScreen />
        }
      ]
    },
    {
      path: "*",
      element: <Navigate to="/" replace />
    }
  ]);
};
